import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  User, DollarSign, MapPin, Clock, ChevronRight, ChevronLeft,
  GripVertical, Loader2, ArrowRight, Home, CalendarPlus,
} from 'lucide-react';
import { FollowUpPanel } from '../components/FollowUpPanel';
import { EmptyDashboardState, PageHint } from '../components/ActivationWidgets';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLORS = {
  new_lead: { bg: 'bg-sky-500/10', border: 'border-sky-500/30', text: 'text-sky-400', dot: 'bg-sky-400' },
  contacted: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', dot: 'bg-blue-400' },
  qualified: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  showing_scheduled: { bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-400', dot: 'bg-violet-400' },
  offer_made: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', dot: 'bg-amber-400' },
  under_contract: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', dot: 'bg-orange-400' },
  closed: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', dot: 'bg-green-400' },
  lost: { bg: 'bg-secondary', border: 'border-border', text: 'text-muted-foreground', dot: 'bg-muted-foreground' },
};

const TYPE_COLORS = {
  buyer: 'bg-blue-500/15 text-blue-400',
  seller: 'bg-amber-500/15 text-amber-400',
  renter: 'bg-teal-500/15 text-teal-400',
  investor: 'bg-purple-500/15 text-purple-400',
  unknown: 'bg-secondary text-muted-foreground',
};

const MOTIVATION_BADGE = {
  hot: 'bg-red-500/15 text-red-400',
  warm: 'bg-amber-500/15 text-amber-400',
  cold: 'bg-cyan-500/15 text-cyan-400',
  unknown: 'bg-secondary text-muted-foreground',
};

function formatBudget(min, max) {
  const fmt = (v) => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${(v / 1000).toFixed(0)}K`;
  if (min && max) return `${fmt(min)} - ${fmt(max)}`;
  if (max) return `Up to ${fmt(max)}`;
  if (min) return `From ${fmt(min)}`;
  return null;
}

function LeadCard({ lead, onMove, onSelect }) {
  const budget = formatBudget(lead.budget_min, lead.budget_max) || lead.budget;
  const area = lead.target_area || lead.preferred_area;
  const motivation = lead.motivation_level || lead.motivation || 'unknown';
  const hasListings = (lead.interested_listings || []).length > 0;

  return (
    <Card
      data-testid={`pipeline-card-${lead.id}`}
      className="bg-card/80 border-border hover:border-border transition-all cursor-pointer group p-3"
      onClick={() => onSelect(lead)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="shrink-0 h-8 w-8 rounded-full bg-muted flex items-center justify-center">
            <User size={14} className="text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{lead.name}</p>
            <Badge className={`text-[10px] px-1.5 py-0 mt-0.5 ${TYPE_COLORS[lead.lead_type] || TYPE_COLORS.unknown}`}>
              {lead.lead_type}
            </Badge>
          </div>
        </div>
        <Badge className={`text-[10px] shrink-0 ${MOTIVATION_BADGE[motivation] || MOTIVATION_BADGE.unknown}`}>
          {motivation}
        </Badge>
        {hasListings && (
          <Home size={12} className="shrink-0 text-violet-400" title="Has property interest" />
        )}
      </div>

      <div className="mt-2 space-y-1">
        {budget && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <DollarSign size={11} className="text-emerald-500 shrink-0" />
            <span className="truncate">{budget}</span>
          </div>
        )}
        {area && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin size={11} className="text-blue-400 shrink-0" />
            <span className="truncate">{area}</span>
          </div>
        )}
        {lead.timeline && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock size={11} className="text-amber-400 shrink-0" />
            <span className="truncate">{lead.timeline}</span>
          </div>
        )}
      </div>

      {/* Quick move buttons */}
      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onMove(lead, 'prev'); }}
          className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          data-testid={`move-prev-${lead.id}`}
          title="Move to previous stage"
        >
          <ChevronLeft size={14} />
        </button>
        <GripVertical size={12} className="text-muted-foreground mx-auto" />
        <button
          onClick={(e) => { e.stopPropagation(); onMove(lead, 'next'); }}
          className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          data-testid={`move-next-${lead.id}`}
          title="Move to next stage"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </Card>
  );
}

function StageColumn({ stage, label, leads, color, onMove, onSelect }) {
  return (
    <div className="flex flex-col min-w-[260px] max-w-[300px] shrink-0" data-testid={`stage-column-${stage}`}>
      <div className={`flex items-center gap-2 px-3 py-2 rounded-t-lg border-b-2 ${color.border} ${color.bg}`}>
        <span className={`h-2 w-2 rounded-full ${color.dot}`} />
        <h3 className={`text-sm font-semibold ${color.text}`}>{label}</h3>
        <span className="ml-auto text-xs text-muted-foreground font-mono">{leads.length}</span>
      </div>
      <ScrollArea className="flex-1 max-h-[calc(100vh-220px)]">
        <div className="p-2 space-y-2">
          {leads.length === 0 ? (
            <div className="py-8 text-center text-xs text-muted-foreground">No leads</div>
          ) : (
            leads.map((lead) => (
              <LeadCard key={lead.id} lead={lead} onMove={onMove} onSelect={onSelect} />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default function PipelinePage() {
  const { token } = useAuth();
  const [board, setBoard] = useState({});
  const [stages, setStages] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchBoard = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leads/pipeline/board`, { headers });
      setBoard(data.board);
      setLabels(data.labels);
      if (stages.length === 0) {
        setStages(Object.keys(data.board));
      }
    } catch {
      toast.error('Failed to load pipeline');
    }
    setLoading(false);
  }, [token]); // eslint-disable-line

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const moveLead = async (lead, direction) => {
    const currentIdx = stages.indexOf(lead.assigned_stage || 'new_lead');
    const newIdx = direction === 'next' ? currentIdx + 1 : currentIdx - 1;
    if (newIdx < 0 || newIdx >= stages.length) return;
    const newStage = stages[newIdx];

    // Optimistic update
    setBoard(prev => {
      const updated = { ...prev };
      const oldStage = lead.assigned_stage || 'new_lead';
      updated[oldStage] = (updated[oldStage] || []).filter(l => l.id !== lead.id);
      updated[newStage] = [{ ...lead, assigned_stage: newStage }, ...(updated[newStage] || [])];
      return updated;
    });

    try {
      await axios.patch(`${API}/leads/${lead.id}/stage`, { stage: newStage }, { headers });
    } catch {
      toast.error('Failed to move lead');
      fetchBoard();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-muted-foreground" size={24} />
      </div>
    );
  }

  const totalLeads = Object.values(board).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <div className="h-full flex flex-col" data-testid="pipeline-page">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div>
          <h1 className="text-lg font-semibold text-foreground" data-testid="pipeline-title">Deal Pipeline</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {totalLeads} leads across {stages.length} stages
          </p>
        </div>
      </div>

      <div className="px-4 pt-3">
        <PageHint id="pipeline-intro" text="Leads move through stages as your AI qualifies them. Drag or click arrows to manually advance deals." type="info" />
      </div>

      {totalLeads === 0 ? (
        <EmptyDashboardState type="pipeline" />
      ) : (
      <div className="flex-1 overflow-x-auto">
        <div className="flex gap-3 p-4 min-h-full" data-testid="pipeline-board">
          {stages.map(stage => (
            <StageColumn
              key={stage}
              stage={stage}
              label={labels[stage] || stage}
              leads={board[stage] || []}
              color={STAGE_COLORS[stage] || STAGE_COLORS.new_lead}
              onMove={moveLead}
              onSelect={setSelectedLead}
            />
          ))}
        </div>
      </div>
      )}

      {/* Lead Detail Slide-over */}
      {selectedLead && (
        <LeadDetailSlideOver
          lead={selectedLead}
          stages={stages}
          labels={labels}
          token={token}
          onClose={() => setSelectedLead(null)}
          onStageChange={(lead, stage) => {
            moveLead({ ...lead, assigned_stage: lead.assigned_stage }, 'next');
            setSelectedLead(null);
            fetchBoard();
          }}
          onUpdate={() => { fetchBoard(); setSelectedLead(null); }}
        />
      )}
    </div>
  );
}

function LeadDetailSlideOver({ lead, stages, labels, token, onClose, onUpdate }) {
  const [stageVal, setStageVal] = useState(lead.assigned_stage || 'new_lead');
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showings, setShowings] = useState([]);
  const [listings, setListings] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [showingTime, setShowingTime] = useState('');
  const [showingListing, setShowingListing] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const budget = formatBudget(lead.budget_min, lead.budget_max) || lead.budget;
  const motivation = lead.motivation_level || lead.motivation || 'unknown';
  const qual = lead.qualification_completeness || 0;

  useEffect(() => {
    const fetchExtra = async () => {
      try {
        const [showRes, listRes] = await Promise.all([
          axios.get(`${API}/showings?lead_id=${lead.id}`, { headers }),
          axios.get(`${API}/listings`, { headers }),
        ]);
        setShowings(showRes.data || []);
        setAllListings(listRes.data || []);
        const interested = lead.interested_listings || [];
        if (interested.length > 0) {
          setListings((listRes.data || []).filter(l => interested.includes(l.id)));
        }
      } catch { /* silent */ }
    };
    fetchExtra();
  }, [lead.id]); // eslint-disable-line

  const scheduleShowing = async () => {
    if (!showingTime) { toast.error('Pick a time'); return; }
    setScheduling(true);
    try {
      await axios.post(`${API}/showings`, {
        lead_id: lead.id,
        listing_id: showingListing || null,
        showing_time: new Date(showingTime).toISOString(),
        duration_minutes: 30,
      }, { headers });
      toast.success('Showing scheduled');
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to schedule');
    }
    setScheduling(false);
  };

  const saveStage = async () => {
    if (stageVal === lead.assigned_stage) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/leads/${lead.id}/stage`, { stage: stageVal }, { headers });
      toast.success('Stage updated');
      onUpdate();
    } catch { toast.error('Failed to update stage'); }
    setSaving(false);
  };

  const qualFields = [
    { label: 'Type', value: lead.lead_type, show: lead.lead_type && lead.lead_type !== 'unknown' },
    { label: 'Budget', value: budget },
    { label: 'Area', value: lead.target_area || lead.preferred_area },
    { label: 'Bedrooms', value: lead.bedrooms },
    { label: 'Bathrooms', value: lead.bathrooms },
    { label: 'Timeline', value: lead.timeline },
    { label: 'Financing', value: lead.financing_status },
    { label: 'Pre-approved', value: lead.preapproved, show: lead.preapproved && lead.preapproved !== 'unknown' },
    { label: 'Property to Sell', value: lead.property_to_sell, show: lead.property_to_sell && lead.property_to_sell !== 'unknown' },
    { label: 'Motivation', value: motivation, show: motivation !== 'unknown' },
    { label: 'Language', value: lead.preferred_language || lead.language_preference },
    { label: 'Source', value: lead.source },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-end" onClick={onClose} data-testid="lead-detail-slideOver">
      <div className="w-full sm:max-w-md bg-background border-l border-border h-full overflow-y-auto animate-in slide-in-from-right" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border z-10 p-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground truncate" data-testid="detail-lead-name">{lead.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={`text-[10px] ${TYPE_COLORS[lead.lead_type] || TYPE_COLORS.unknown}`}>{lead.lead_type}</Badge>
                <Badge className={`text-[10px] ${MOTIVATION_BADGE[motivation] || MOTIVATION_BADGE.unknown}`}>{motivation}</Badge>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted transition-colors">
              <ArrowRight size={18} />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-5">
          {/* Pipeline Stage Selector */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Pipeline Stage</label>
            <div className="flex gap-2">
              <select
                value={stageVal}
                onChange={e => setStageVal(e.target.value)}
                data-testid="stage-selector"
                className="flex-1 bg-card border border-border text-foreground text-sm rounded-lg px-3 py-2 focus:border-emerald-500 outline-none"
              >
                {stages.map(s => <option key={s} value={s}>{labels[s] || s}</option>)}
              </select>
              <button
                onClick={saveStage}
                disabled={saving || stageVal === lead.assigned_stage}
                data-testid="save-stage-btn"
                className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded-lg disabled:opacity-40 transition-colors"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border">
            {[{ key: 'details', label: 'Details' }, { key: 'showings', label: 'Showings' }, { key: 'followup', label: 'Follow-Up' }].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`tab-${tab.key}`}
                className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-emerald-500 text-emerald-400'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'followup' ? (
            <FollowUpPanel leadId={lead.id} leadName={lead.name} />
          ) : activeTab === 'showings' ? (
            <div className="space-y-4" data-testid="showings-tab">
              {/* Schedule Showing */}
              <div className="border border-border rounded-lg p-3 space-y-2">
                <h4 className="text-xs font-medium text-muted-foreground">Schedule Showing</h4>
                <div className="flex gap-2">
                  <input type="datetime-local" value={showingTime} onChange={e => setShowingTime(e.target.value)} data-testid="showing-time-input"
                    className="flex-1 bg-card border border-border text-foreground text-sm rounded-lg px-3 py-2 outline-none" />
                  {allListings.length > 0 && (
                    <select value={showingListing} onChange={e => setShowingListing(e.target.value)} data-testid="showing-listing-select"
                      className="bg-card border border-border text-foreground text-sm rounded-lg px-2 py-2 outline-none max-w-[140px]">
                      <option value="">No listing</option>
                      {allListings.map(l => <option key={l.id} value={l.id}>{l.address}</option>)}
                    </select>
                  )}
                </div>
                <button onClick={scheduleShowing} disabled={scheduling} data-testid="schedule-showing-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-foreground text-xs font-medium rounded-lg disabled:opacity-50 transition-colors">
                  {scheduling ? <Loader2 size={13} className="animate-spin" /> : <CalendarPlus size={13} />}
                  Schedule Showing
                </button>
              </div>

              {/* Upcoming Showings */}
              {showings.filter(s => s.status === 'scheduled').length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-muted-foreground mb-2">Upcoming</h4>
                  {showings.filter(s => s.status === 'scheduled').map(s => (
                    <div key={s.id} className="flex items-center gap-2 py-2 border-b border-border last:border-0" data-testid={`showing-${s.id}`}>
                      <CalendarPlus size={14} className="text-violet-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-foreground">{new Date(s.showing_time).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        {s.listing_address && <p className="text-xs text-muted-foreground truncate">{s.listing_address}</p>}
                      </div>
                      <Badge className="text-[10px] bg-violet-500/15 text-violet-400">{s.duration_minutes}min</Badge>
                    </div>
                  ))}
                </div>
              )}

              {/* Interested Listings */}
              {listings.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-muted-foreground mb-2">Interested Properties</h4>
                  {listings.map(l => (
                    <div key={l.id} className="flex items-center gap-2 py-2 border-b border-border last:border-0" data-testid={`interested-listing-${l.id}`}>
                      <Home size={14} className="text-emerald-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-foreground truncate">{l.address}</p>
                        <p className="text-xs text-muted-foreground">{l.beds}bd/{l.baths}ba — ${l.price ? l.price.toLocaleString() : '0'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showings.length === 0 && listings.length === 0 && (
                <div className="text-center py-6 text-muted-foreground">
                  <CalendarPlus size={24} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No showings yet</p>
                </div>
              )}
            </div>
          ) : (
          <>

          {/* Qualification Progress */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-muted-foreground">Qualification</span>
              <span className="text-xs font-mono text-muted-foreground">{qual}%</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${qual >= 75 ? 'bg-emerald-500' : qual >= 40 ? 'bg-amber-500' : 'bg-muted-foreground'}`}
                style={{ width: `${qual}%` }}
                data-testid="qualification-bar"
              />
            </div>
          </div>

          {/* Qualification Fields */}
          <div>
            <h3 className="text-xs font-medium text-muted-foreground mb-2">Details</h3>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {qualFields.filter(f => f.show !== false && f.value).map(f => (
                <div key={f.label}>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{f.label}</span>
                  <p className="text-sm text-foreground truncate">{String(f.value)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact */}
          {(lead.phone || lead.email) && (
            <div>
              <h3 className="text-xs font-medium text-muted-foreground mb-2">Contact</h3>
              {lead.phone && <p className="text-sm text-foreground">{lead.phone}</p>}
              {lead.email && <p className="text-sm text-foreground mt-1">{lead.email}</p>}
            </div>
          )}

          {/* Last Call Summary */}
          {lead.last_ai_summary && (
            <div>
              <h3 className="text-xs font-medium text-muted-foreground mb-2">Last Call Summary</h3>
              <p className="text-sm text-foreground leading-relaxed">{lead.last_ai_summary}</p>
            </div>
          )}

          {/* Next Action */}
          {(lead.next_action || lead.recommended_next_action) && (
            <div>
              <h3 className="text-xs font-medium text-muted-foreground mb-2">Recommended Next Action</h3>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                <p className="text-sm text-emerald-300">{lead.next_action || lead.recommended_next_action}</p>
              </div>
            </div>
          )}

          {/* Follow-up Status */}
          {lead.last_followup_status && (
            <div>
              <h3 className="text-xs font-medium text-muted-foreground mb-2">Follow-up Status</h3>
              <Badge className="text-xs bg-muted text-foreground">{lead.last_followup_status}</Badge>
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}
